/* eslint-disable react/jsx-max-props-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import {
  TIPOS_COM_VEICULO,
  getTipoNotaLabel,
} from '../../../constants/tipoNota';
import { tableV2StackedCellContentSx } from '../../../components/v2/tableV2Styles';

export { TIPOS_COM_VEICULO, getTipoNotaLabel };

export const getTipoPagamentoLabel = (tipoPagamento) => {
  if (tipoPagamento === 'CARTAO_CORPORATIVO') return 'Cartão Corporativo';
  if (tipoPagamento === 'APORTE') return 'Aporte';
  return 'N/A';
};

const formatSimNao = (value) =>
  value === true || value === 'true' ? 'Sim' : 'Não';

/** Sim/Não quando informado; "—" quando ainda não classificado (null/undefined). */
const formatOptionalSimNao = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return formatSimNao(value);
};

const CaptionLabeledLine = ({ label, children }) => (
  <Typography variant="caption" component="div" sx={{ whiteSpace: 'nowrap' }}>
    <Box component="span" sx={{ fontWeight: 'bold' }}>
      {label}{' '}
    </Box>
    {children}
  </Typography>
);

CaptionLabeledLine.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const EntityLink = ({ label, onOpen, tooltip }) => {
  if (!onOpen) {
    return label;
  }

  return (
    <Tooltip title={tooltip}>
      <Link
        component="button"
        type="button"
        variant="caption"
        underline="hover"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
      >
        {label}
      </Link>
    </Tooltip>
  );
};

EntityLink.propTypes = {
  label: PropTypes.string.isRequired,
  onOpen: PropTypes.func,
  tooltip: PropTypes.string,
};

export const renderTipoNotaCell = (row) => {
  const showVeiculo =
    TIPOS_COM_VEICULO.includes(row.tipo) && Boolean(row.veiculoPlaca);

  return (
    <Box sx={tableV2StackedCellContentSx}>
      <Typography variant="body2">{getTipoNotaLabel(row.tipo)}</Typography>
      {showVeiculo && (
        <CaptionLabeledLine label="Veículo:">{row.veiculoPlaca}</CaptionLabeledLine>
      )}
    </Box>
  );
};

export const renderTipoPagamentoCell = (row, onOpenCartao) => {
  const isCartao = row.tipoPagamento === 'CARTAO_CORPORATIVO';
  const cartaoCodigo = row.cartaoCodigo || row.cartao?.codigo || null;
  const cartaoId = row.cartaoId || row.cartao?.id || null;
  const canLink = Boolean(cartaoId && onOpenCartao);

  return (
    <Box sx={tableV2StackedCellContentSx}>
      <Typography variant="body2">{getTipoPagamentoLabel(row.tipoPagamento)}</Typography>
      {isCartao && cartaoCodigo && (
        <CaptionLabeledLine label="Cartão:">
          <EntityLink
            label={cartaoCodigo}
            onOpen={canLink ? () => onOpenCartao(cartaoId) : undefined}
            tooltip="Abrir cartão"
          />
        </CaptionLabeledLine>
      )}
    </Box>
  );
};

export const renderValorCell = (row) => {
  const valorLabel =
    row.valor != null
      ? row.valor.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
      : '—';

  return (
    <Box sx={tableV2StackedCellContentSx}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {valorLabel}
      </Typography>
      <CaptionLabeledLine label="Inspeção:">
        {formatSimNao(row.inspecao)}
      </CaptionLabeledLine>
    </Box>
  );
};

export const renderOutrasInformacoesCell = (row) => (
  <Box sx={tableV2StackedCellContentSx}>
    <CaptionLabeledLine label="Cupom fiscal:">
      {formatSimNao(row.possuiCupom)}
    </CaptionLabeledLine>
    <CaptionLabeledLine label="Fora MG:">
      {formatOptionalSimNao(row.foraEstado)}
    </CaptionLabeledLine>
    <CaptionLabeledLine label="Imposto empresa:">
      {formatOptionalSimNao(row.possuiImpostoEmpresa)}
    </CaptionLabeledLine>
  </Box>
);

export const renderInspetorCell = (row) => {
  const observador = row.inspetorAuxiliarNome || row.inspetorAuxiliar?.nome;

  return (
    <Box sx={tableV2StackedCellContentSx}>
      <Typography variant="body2">{row.usuarioNome || '—'}</Typography>
      {observador ? (
        <CaptionLabeledLine label="Observador/Ajudante:">
          {observador}
        </CaptionLabeledLine>
      ) : null}
    </Box>
  );
};
